// src/app/components/sso-connections-card.tsx
//
// Admin-only list + editor for customer SSO connections: a customer's own
// Okta org (or any OIDC IdP), bound to the email domains they own. Backed by
// /auth/admin/sso/connections.
//
// On the sign-in page, "Sign in with SSO" looks up the typed email's domain and
// sends the user to that customer's IdP. Users who sign in this way are never
// admins. Whether a first sign-in may create an account is set per connection
// here, along with the brands such an account starts with — granted once, at
// creation, as ordinary assignments that then show in User Management and
// Associate Brands. "Apply to existing users" is the deliberate counterpart
// for people who signed in before the brands were set.

import { useEffect, useMemo, useState } from "react";
import { Loader2, AlertCircle, Bell, Building2, Check, Plus, Pencil, Search, Trash2, Users } from "lucide-react";

import { Input } from "./ui/input";
import { Button, IconButton, Toggle } from "./kit";
import { Label } from "./ui/label";
import { Alert, AlertDescription } from "./ui/alert";
import { ConfirmDialog } from "./ui/confirm-dialog";
import { authApi, type SsoConnection, type SsoConnectionInput } from "@/api/auth";
import { brandsApi, type AssignableBrand } from "@/api/brands";
import { notify } from "@/app/lib/notify";

const EMPTY: SsoConnectionInput = {
  name: "", issuer: "", client_id: "", redirect_uri: "",
  domains: [], default_brands: [], jit_enabled: false, enabled: true,
};

function parseDomains(raw: string): string[] {
  return raw.split(/[\s,]+/).map((d) => d.trim()).filter(Boolean);
}

const hintStyle = { marginTop: 4, fontSize: "var(--text-xs)", color: "var(--muted-foreground)" } as const;
const labelStyle = { display: "block", marginBottom: 6, fontSize: "var(--text-sm)" } as const;

export function SsoConnectionsCard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connections, setConnections] = useState<SsoConnection[]>([]);

  // null = not editing; "" = adding a new one; otherwise the id being edited.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<SsoConnectionInput>(EMPTY);
  const [domainsText, setDomainsText] = useState("");
  const [secret, setSecret] = useState("");            // write-only; blank = keep current
  const [secretSet, setSecretSet] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<SsoConnection | null>(null);

  // Brands to offer as defaults, loaded once (same source as the admin
  // create-user page, so the names match what Associate Brands assigns).
  const [brands, setBrands] = useState<AssignableBrand[]>([]);
  const [brandQuery, setBrandQuery] = useState("");
  const [applyingId, setApplyingId] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    authApi.listSsoConnections()
      .then((list) => alive && setConnections(list))
      .catch((e) => alive && setError(e?.message || "Failed to load SSO connections"))
      .finally(() => alive && setLoading(false));
    // A missing brand list only costs the picker, never the rest of the card.
    brandsApi.assignable()
      .then((list) => alive && setBrands(list))
      .catch(() => undefined);
    return () => { alive = false; };
  }, []);

  const visibleBrands = useMemo(() => {
    const q = brandQuery.trim().toLowerCase();
    if (!q) return brands;
    return brands.filter((b) => b.brand_name.toLowerCase().includes(q));
  }, [brands, brandQuery]);

  const toggleBrand = (name: string) =>
    setDraft((d) => ({
      ...d,
      default_brands: d.default_brands.includes(name)
        ? d.default_brands.filter((b) => b !== name)
        : [...d.default_brands, name],
    }));

  const startEdit = (conn: SsoConnection | null) => {
    setError(null);
    setEditingId(conn ? conn.id : "");
    setBrandQuery("");
    setDraft(conn ? {
      name: conn.name, issuer: conn.issuer, client_id: conn.client_id,
      redirect_uri: conn.redirect_uri, domains: conn.domains,
      default_brands: conn.default_brands ?? [],
      jit_enabled: conn.jit_enabled, enabled: conn.enabled,
    } : EMPTY);
    setDomainsText(conn ? conn.domains.join(", ") : "");
    setSecret("");
    setSecretSet(conn ? conn.client_secret_set : false);
  };

  /** What a sync did, for the notification. */
  const syncLine = (r: {
    users_matched: number; users_updated: number;
    brands_added: number; brands_removed: number;
  }, name: string): string => {
    if (r.users_updated === 0) {
      return `No change for the ${r.users_matched} existing ${name} user${
        r.users_matched === 1 ? "" : "s"} — they already match.`;
    }
    const parts: string[] = [];
    if (r.brands_added) parts.push(`${r.brands_added} brand${r.brands_added === 1 ? "" : "s"} added`);
    if (r.brands_removed) parts.push(`${r.brands_removed} withdrawn`);
    return `${parts.join(", ")} across ${r.users_updated} of ${r.users_matched} existing ` +
      `${name} user${r.users_matched === 1 ? "" : "s"}.`;
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    const payload: SsoConnectionInput = {
      ...draft,
      name: draft.name.trim(),
      issuer: draft.issuer.trim(),
      client_id: draft.client_id.trim(),
      redirect_uri: draft.redirect_uri.trim(),
      domains: parseDomains(domainsText),
    };
    // Only send the secret if the admin typed one (blank = keep current).
    if (secret.trim()) payload.client_secret = secret.trim();
    try {
      if (editingId) {
        const saved = await authApi.updateSsoConnection(editingId, payload);
        setConnections((list) => list.map((c) => (c.id === saved.id ? saved : c)));
        notify.success(`${saved.name} saved`, {
          description: saved.brands_synced
            ? syncLine(saved.brands_synced, saved.name)
            : undefined,
        });
      } else {
        if (!payload.client_secret) throw new Error("Client secret is required");
        const saved = await authApi.createSsoConnection(payload);
        setConnections((list) => [...list, saved]);
        notify.success(`${saved.name} added`, {
          description: saved.default_brands.length
            ? `New accounts at ${saved.domains.join(", ")} will start with ${saved.default_brands.join(", ")}.`
            : undefined,
        });
      }
      setSecret("");
      setEditingId(null);
    } catch (e: any) {
      setError(e?.message || "Failed to save SSO connection");
      notify.error(e, { fallback: "Failed to save SSO connection" });
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (conn: SsoConnection, enabled: boolean) => {
    setError(null);
    try {
      const saved = await authApi.updateSsoConnection(conn.id, { enabled });
      setConnections((list) => list.map((c) => (c.id === saved.id ? saved : c)));
      notify.success(`${saved.name} ${enabled ? "enabled" : "disabled"}`, {
        description: enabled
          ? undefined
          : "Nobody can sign in through this connection until it is switched back on.",
      });
    } catch (e: any) {
      setError(e?.message || "Failed to update SSO connection");
      notify.error(e, { fallback: "Failed to update SSO connection" });
    }
  };

  const applyBrands = async (conn: SsoConnection) => {
    setError(null);
    setApplyingId(conn.id);
    try {
      const r = await authApi.applySsoConnectionBrands(conn.id);
      notify.success(`${conn.name} brands synced`, { description: syncLine(r, conn.name) });
    } catch (e: any) {
      setError(e?.message || "Failed to sync brands");
      notify.error(e, { fallback: "Failed to sync brands" });
    } finally {
      setApplyingId(null);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    const target = pendingDelete;
    setPendingDelete(null);
    setError(null);
    try {
      await authApi.deleteSsoConnection(target.id);
      setConnections((list) => list.filter((c) => c.id !== target.id));
      notify.success(`${target.name} deleted`, {
        description: "Their accounts and brand access are kept.",
      });
    } catch (e: any) {
      setError(e?.message || "Failed to delete SSO connection");
      notify.error(e, { fallback: "Failed to delete SSO connection" });
    }
  };

  const field = (
    label: string, value: string, onChange: (v: string) => void,
    placeholder = "", hint?: string, type = "text",
  ) => (
    <div style={{ marginBottom: 14 }}>
      <Label style={labelStyle}>{label}</Label>
      <Input type={type} value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} disabled={saving} />
      {hint && <div style={hintStyle}>{hint}</div>}
    </div>
  );

  const switchRow = (label: string, hint: string, checked: boolean, onChange: (v: boolean) => void) => (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 14 }}>
      <Toggle checked={checked} onChange={onChange} label={label} labelHidden state={saving ? "disabled" : undefined} />
      <div>
        <div style={{ fontSize: "var(--text-sm)" }}>{label}</div>
        <div style={hintStyle}>{hint}</div>
      </div>
    </div>
  );

  const summary = (conn: SsoConnection) => [
    conn.domains.join(", "),
    conn.jit_enabled ? "creates accounts on first sign-in" : "existing users only",
    conn.default_brands?.length ? `brands: ${conn.default_brands.join(", ")}` : "no default brands",
    conn.client_secret_set ? null : "no client secret",
  ].filter(Boolean).join(" · ");

  return (
    <div style={{
      border: "1px solid var(--border)", borderRadius: 12,
      padding: 24, background: "var(--card)", maxWidth: 640,
      fontFamily: "var(--font-sans)",
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div>
          <h3 style={{ margin: "0 0 4px", fontSize: "var(--text-lg)", color: "var(--foreground)" }}>
            Customer SSO connections
          </h3>
          <p style={{ margin: "0 0 18px", fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
            Let a customer's users sign in with their own Okta (or any OIDC provider). Users are
            routed by the domain of the email they enter, and are never admins.
          </p>
        </div>
        {editingId === null && !loading && (
          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
            {/* A notification someone dismissed before reading it is not
                gone: this brings the last one back. */}
            <IconButton
              icon={Bell}
              label="Show the last notification again"
              onClick={() => {
                if (!notify.showLast()) notify.info("Nothing to show yet.");
              }}
            />
            <Button icon={Plus} onClick={() => startEdit(null)}>Add</Button>
          </div>
        )}
      </div>

      {error && (
        <Alert variant="destructive" style={{ marginBottom: 16 }}>
          <AlertCircle style={{ width: 16, height: 16 }} />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10, color: "var(--muted-foreground)" }}>
          <Loader2 className="animate-spin" style={{ width: 18, height: 18 }} />
          Loading connections…
        </div>
      ) : editingId !== null ? (
        <div>
          {field("Customer name", draft.name, (v) => setDraft({ ...draft, name: v }), "Acme Corp")}
          {field("Issuer URL", draft.issuer, (v) => setDraft({ ...draft, issuer: v }),
            "https://acme.okta.com",
            "The customer's Okta org URL (or a custom authorization server). Must be https.")}
          {field("Client ID", draft.client_id, (v) => setDraft({ ...draft, client_id: v }),
            "0oa1b2c3d4…", "From the customer's Okta Web Application integration.")}
          {field(
            secretSet ? "Client secret (set — leave blank to keep)" : "Client secret",
            secret, setSecret,
            secretSet ? "••••••••" : "From the same Okta integration",
            "Stored encrypted and never shown again.", "password",
          )}
          {field("Redirect URI (optional)", draft.redirect_uri,
            (v) => setDraft({ ...draft, redirect_uri: v }),
            `${window.location.origin}/`,
            "Leave blank to use this site's usual sign-in address. Must match the customer's Okta app exactly.")}
          {field("Email domains", domainsText, setDomainsText, "acme.com, acme.co.uk",
            "Only emails at these domains can sign in through this connection. A domain can belong to one connection.")}

          <div style={{ marginBottom: 14 }}>
            <Label style={labelStyle}>Brands for these domains</Label>

            {/* Deliberately the same control as Global Settings -> Associate
                Brands: an admin picking brands should not meet two different
                pickers in the same product. */}
            <div className="relative" style={{ marginBottom: 10 }}>
              <Search style={{
                position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
                width: 15, height: 15, color: "var(--muted-foreground)", pointerEvents: "none",
              }} />
              <input
                type="search"
                value={brandQuery}
                disabled={saving}
                onChange={(e) => setBrandQuery(e.target.value)}
                placeholder="Search saved brands…"
                style={{
                  width: "100%", padding: "10px 12px 10px 36px", borderRadius: 9,
                  border: "1px solid var(--border)", backgroundColor: "var(--input-background)",
                  fontSize: 13, outline: "none", boxSizing: "border-box",
                  color: "var(--foreground)",
                }}
              />
            </div>

            {visibleBrands.length === 0 ? (
              <div className="p-8 text-center" style={{
                border: "1px dashed var(--border)", borderRadius: 12,
                color: "var(--muted-foreground)",
              }}>
                <Building2 style={{ width: 32, height: 32, margin: "0 auto 8px", opacity: 0.4 }} />
                <div style={{ fontSize: 13 }}>
                  {brands.length === 0
                    ? "No saved brands found. Brands appear here once they exist in onboarding or brand monitoring."
                    : "No brands match that search."}
                </div>
              </div>
            ) : (
              <div style={{
                display: "flex", flexDirection: "column", gap: 6,
                maxHeight: 280, overflowY: "auto", paddingRight: 4,
              }}>
                {visibleBrands.map((b) => {
                  const isOn = draft.default_brands.includes(b.brand_name);
                  return (
                    <button
                      key={b.brand_name}
                      type="button"
                      aria-pressed={isOn}
                      disabled={saving}
                      onClick={() => toggleBrand(b.brand_name)}
                      style={{
                        display: "flex", alignItems: "center", gap: 12,
                        padding: "11px 14px", borderRadius: 10, cursor: "pointer",
                        textAlign: "left", transition: "all 0.15s",
                        border: `1px solid ${isOn ? "var(--alpha-teal)" : "var(--border)"}`,
                        backgroundColor: isOn ? "var(--alpha-teal-10)" : "var(--muted)",
                      }}
                    >
                      <div style={{
                        width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        border: `1px solid ${isOn ? "var(--alpha-teal)" : "var(--border)"}`,
                        backgroundColor: isOn ? "var(--alpha-teal)" : "transparent",
                      }}>
                        {isOn && <Check style={{ width: 14, height: 14, color: "white" }} />}
                      </div>
                      <span style={{
                        flex: 1, minWidth: 0, fontSize: 13,
                        fontWeight: isOn ? 600 : 500,
                        color: isOn ? "var(--alpha-teal)" : "var(--foreground)",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>
                        {b.brand_name}
                      </span>
                      <span style={{
                        fontSize: 11, color: "var(--muted-foreground)",
                        textTransform: "capitalize", flexShrink: 0,
                      }}>
                        {b.source}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            <div style={hintStyle}>
              {draft.default_brands.length === 0
                ? "None. Accounts from this connection start with no brands, and saving withdraws any this connection granted before."
                : `Everyone at these domains gets ${draft.default_brands.join(", ")} — new accounts when they are created, and existing ones when you save. Brands an admin assigned by hand are left alone.`}
            </div>
          </div>

          {switchRow(
            "Create accounts on first sign-in",
            draft.jit_enabled
              ? (draft.default_brands.length
                  ? `Anyone at these domains whom the customer assigns in Okta gets an Upmode account when they first sign in, holding ${draft.default_brands.join(", ")}.`
                  : "Anyone at these domains whom the customer assigns in Okta gets an Upmode account when they first sign in — with no brands until you assign them in Associate Brands.")
              : "Off: only people who already have an Upmode account can sign in. Others are told to ask you.",
            draft.jit_enabled, (v) => setDraft({ ...draft, jit_enabled: v }),
          )}

          {switchRow("Enabled", "When off, nobody can sign in through this connection.",
            draft.enabled, (v) => setDraft({ ...draft, enabled: v }))}

          <div style={{ display: "flex", gap: 8 }}>
            <Button onClick={handleSave} disabled={saving} loading={saving} loadingLabel="Saving…">
              {editingId ? "Save connection" : "Add connection"}
            </Button>
            <Button variant="secondary" onClick={() => setEditingId(null)} disabled={saving}>
              Cancel
            </Button>
          </div>
        </div>
      ) : connections.length === 0 ? (
        <div style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
          No customer connections yet.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {connections.map((conn) => (
            <div
              key={conn.id}
              style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "12px 14px", borderRadius: 8,
                border: "1px solid var(--border)", background: "var(--input-background)",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: "var(--text-base)", fontWeight: "var(--font-weight-semibold)" }}>
                  {conn.name}
                </div>
                <div style={{
                  fontSize: "var(--text-xs)", color: "var(--muted-foreground)",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {summary(conn)}
                </div>
              </div>
              <Toggle
                checked={conn.enabled}
                onChange={(v) => toggleEnabled(conn, v)}
                label={`${conn.enabled ? "Disable" : "Enable"} ${conn.name}`}
                labelHidden
              />
              {conn.default_brands?.length > 0 && (
                <IconButton
                  icon={Users}
                  label={`Sync brands for existing ${conn.name} users`}
                  title={`Bring existing ${conn.name} users in line with ${conn.default_brands.join(", ")}`}
                  disabled={applyingId === conn.id}
                  onClick={() => applyBrands(conn)}
                />
              )}
              <IconButton icon={Pencil} label={`Edit ${conn.name}`} onClick={() => startEdit(conn)} />
              <IconButton icon={Trash2} label={`Delete ${conn.name}`} onClick={() => setPendingDelete(conn)} />
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => { if (!open) setPendingDelete(null); }}
        onConfirm={handleDelete}
        title={`Delete ${pendingDelete?.name ?? "connection"}?`}
        description="Its users will no longer be able to sign in with SSO. Their accounts and brand access are kept."
        confirmLabel="Delete"
        destructive
      />
    </div>
  );
}
